import { useMemo } from "react";
import { TAILWIND_PROPERTIES } from "@/lib/tailwind";
import type {
  ClassType,
  ClassTypeWithPropertyType,
} from "@/tailwind/tailwindUtils";
import { ALL_MODIFIERS_SET } from "./useModifiers";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import {
  PropertyType,
  tailwindFilterFunctions,
} from "@/tailwind/tailwindUtils";

const filterFunctionList = Object.values(tailwindFilterFunctions);

// Cache for merged properties to avoid re-computing
let mergedPropertiesCache: ClassTypeWithPropertyType[] | null = null;
let lastCustomClassesLength = 0;

// Cache for filtered properties by PropertyType to avoid re-filtering
const filteredPropertiesCache = new Map<
  PropertyType,
  ClassTypeWithPropertyType[]
>();
let lastMergedCacheVersion = 0;

export const useTailwindProperties = (
  type: PropertyType
): ClassTypeWithPropertyType[] => {
  const tailwindClasses = useCodebaseStore((state) => state.tailwindClasses);

  return useMemo(() => {
    let allProperties = TAILWIND_PROPERTIES;

    if (tailwindClasses && tailwindClasses.length > 0) {
      // Use cache if available and custom classes haven't changed
      if (
        mergedPropertiesCache &&
        lastCustomClassesLength === tailwindClasses.length
      ) {
        allProperties = mergedPropertiesCache;
      } else {
        const tailwindClassSet = new Set<string>();
        const mergedArray: ClassTypeWithPropertyType[] = [];

        // Add standard properties first
        for (const prop of TAILWIND_PROPERTIES) {
          tailwindClassSet.add(prop.tailwind);
          mergedArray.push(prop);
        }

        // Add custom properties, avoiding duplicates
        let customAdded = 0;
        for (const customProp of tailwindClasses) {
          if (!tailwindClassSet.has(customProp.tailwind)) {
            tailwindClassSet.add(customProp.tailwind);
            mergedArray.push(customProp);
            customAdded++;
          }
        }

        // Cache the result and clear filtered cache since merged properties changed
        mergedPropertiesCache = mergedArray;
        lastCustomClassesLength = tailwindClasses.length;
        filteredPropertiesCache.clear();
        ++lastMergedCacheVersion;
        allProperties = mergedArray;
      }
    }

    // Check if we have cached filtered properties for this type
    const cachedFiltered = filteredPropertiesCache.get(type);
    if (cachedFiltered) {
      return cachedFiltered;
    }

    // More efficient filtering for background colors specifically
    let filteredProperties: ClassTypeWithPropertyType[];
    if (type === PropertyType.BACKGROUND_COLOR) {
      // Pre-filter by prefix for better performance
      const bgClasses = allProperties.filter((p) =>
        p.tailwind.startsWith("bg-")
      );

      filteredProperties = bgClasses.filter(tailwindFilterFunctions[type]);
    } else {
      filteredProperties = allProperties.filter(tailwindFilterFunctions[type]);
    }

    // Cache the filtered result
    filteredPropertiesCache.set(type, filteredProperties);

    return filteredProperties;
  }, [type, tailwindClasses]);
};

export const useUnknownTailwindClasses = (classList: string[]): string[] => {
  const tailwindClasses = useCodebaseStore((state) => state.tailwindClasses);

  return useMemo(() => {
    if (!classList) {
      return [];
    }

    return classList.filter((className: string) => {
      if (
        className.startsWith("tempo-") ||
        className.startsWith("arb89-") ||
        className.startsWith("temp-")
      ) {
        return false;
      }

      // Note, all text properties are supported in the styling panel,
      // but it's difficult to filter for which property exactly is being used.
      // So filter here instead of adding to the filter functions
      if (className.startsWith("text-")) {
        return false;
      }

      // Similarly, all border properties are supported
      if (className.startsWith("border-")) {
        return false;
      }

      // All flex properties are supported
      if (className.startsWith("flex")) {
        return false;
      }

      // Strip modifier name from class
      ALL_MODIFIERS_SET.forEach((modifier) => {
        if (className.startsWith(`${modifier}:`)) {
          // Replace from start of string
          className = className.replace(`${modifier}:`, "");
        }
      });

      // If there are custom properties, directly check if the class is in the list
      if (tailwindClasses) {
        // TODO(nhanthai-tailwind): Use set maybe
        if (
          tailwindClasses.find((prop: ClassType) => prop.tailwind === className)
        ) {
          return false;
        }
      }

      return !filterFunctionList.find((filterFn: any) => {
        return filterFn({ tailwind: className, css: "" });
      });
    });
  }, [classList, tailwindClasses]);
};
